import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { AttendantCompanyTypeEnum } from 'constants/loanApplication';

import { selectEntityChangedPropsById } from 'store/loanApplications/selectors';

import { useClosingCompanyHandlers } from './useClosingCompanyHandlers';

// Bundles the Title/Escrow/Seller-Attorney/Buyer-Attorney read model (closing
// companies + their pending changes) with their lookup-change handlers, so
// Content.js only needs one hook call for the whole set.
export const useTitleEscrowAttorneyCompanies = ({
  closingInfo,
  closingCompanies,
  attendantCompanyOptions,
  categoriesIds,
  isSaving,
  setIsLoading,
  setFormRef,
  setIsTouchedRef,
  formDataRef,
}) => {
  const titleCompany = useMemo(
    () => closingCompanies?.find(c => c.closingCompanyType === AttendantCompanyTypeEnum.TITLE),
    [closingCompanies],
  );
  const escrowCompany = useMemo(
    () => closingCompanies?.find(c => c.closingCompanyType === AttendantCompanyTypeEnum.ESCROW),
    [closingCompanies],
  );
  const sellerAttorney = useMemo(
    () =>
      closingCompanies?.find(
        c => c.closingCompanyType === AttendantCompanyTypeEnum.SELLER_ATTORNEY,
      ),
    [closingCompanies],
  );
  const buyerAttorney = useMemo(
    () =>
      closingCompanies?.find(c => c.closingCompanyType === AttendantCompanyTypeEnum.BUYER_ATTORNEY),
    [closingCompanies],
  );

  const titleCompanyPendingChanges = useSelector(
    selectEntityChangedPropsById(`${closingInfo?.loanClosingId}_${AttendantCompanyTypeEnum.TITLE}`),
  );
  const escrowCompanyPendingChanges = useSelector(
    selectEntityChangedPropsById(
      `${closingInfo?.loanClosingId}_${AttendantCompanyTypeEnum.ESCROW}`,
    ),
  );
  const titleLocationPendingChanges = useSelector(
    selectEntityChangedPropsById(titleCompany?.closingCompanyLocationId),
  );
  const escrowLocationPendingChanges = useSelector(
    selectEntityChangedPropsById(escrowCompany?.closingCompanyLocationId),
  );
  const sellerAttorneyPendingChanges = useSelector(
    selectEntityChangedPropsById(
      `${closingInfo?.loanClosingId}_${AttendantCompanyTypeEnum.SELLER_ATTORNEY}`,
    ),
  );
  const buyerAttorneyPendingChanges = useSelector(
    selectEntityChangedPropsById(
      `${closingInfo?.loanClosingId}_${AttendantCompanyTypeEnum.BUYER_ATTORNEY}`,
    ),
  );
  const sellerAttorneyLocationPendingChanges = useSelector(
    selectEntityChangedPropsById(sellerAttorney?.closingCompanyLocationId),
  );
  const buyerAttorneyLocationPendingChanges = useSelector(
    selectEntityChangedPropsById(buyerAttorney?.closingCompanyLocationId),
  );

  const handlers = useClosingCompanyHandlers({
    titleCompany,
    escrowCompany,
    sellerAttorney,
    buyerAttorney,
    attendantCompanyOptions,
    categoriesIds,
    isSaving,
    setIsLoading,
    setFormRef,
    setIsTouchedRef,
    formDataRef,
  });

  return {
    titleCompany,
    escrowCompany,
    sellerAttorney,
    buyerAttorney,
    titleCompanyPendingChanges,
    escrowCompanyPendingChanges,
    titleLocationPendingChanges,
    escrowLocationPendingChanges,
    sellerAttorneyPendingChanges,
    sellerAttorneyLocationPendingChanges,
    buyerAttorneyPendingChanges,
    buyerAttorneyLocationPendingChanges,
    ...handlers,
  };
};
